<template>
    <div
        class="stage-group"
        :class="cClass">
        <div class="container">
            <div
                class="felt"
                :class="{ editing: editTime }">
                <div class="in-step">
                    <template v-if="editTime">
                        <div class="space-x-1">
                            <div class="inline-block w-[194px]">
                                <hts-form-date
                                    v-model="stage.ata"
                                    label="Thời gian vào điểm"
                                    type="datetime"
                                    class="w-full"
                                    :clearable="clearable"
                                    @change="handleChangeTime($event, 'ata')" />
                                <p class="text-red-500">
                  &nbsp;
                                    <span
                                        v-if="hasInError"
                                        class="travel-error">
                                        Thời gian nhập lỗi
                                    </span>
                                </p>
                            </div>
                            <div class="inline-block w-[194px]">
                                <hts-form-date
                                    v-model="stage.atd"
                                    label="Thời gian ra điểm"
                                    type="datetime"
                                    class="w-full"
                                    :clearable="clearable"
                                    @change="handleChangeTime($event, 'atd')" />
                                <p class="text-red-500">
                  &nbsp;
                                    <span
                                        v-if="hasOutError"
                                        class="travel-error">
                                        Thời gian nhập lỗi
                                    </span>
                                </p>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div
                            :class="{
                                'text-main-text': stage.processAlertStatus > 10,
                                'text-error-500': stage.processAlertStatus > 30,
                                'opacity-50': stage.disable
                            }">
                            <template v-if="isSameDate">
                                <p class="text-right font-bold">
                                    <span
                                        class="time-start"
                                        :class="stepClasses">
                                        {{ getStartTime() }}
                                    </span><span>
                                        -
                                    </span><span
                                        class="time-end"
                                        :class="stepClasses">
                                        {{ getEndTime() }}
                                    </span>
                                </p>
                                <p
                                    class="text-right time-end"
                                    :class="stepClasses">
                                    {{ getDateRange() }}
                                </p>
                            </template>
                            <template v-else>
                                <div class="flex justify-end items-center gap-2">
                                    <div>
                                        <p
                                            class="text-right font-bold time-start"
                                            :class="stepClasses">
                                            {{ getStartTime() }}
                                        </p>
                                        <p
                                            class="text-right time-start"
                                            :class="stepClasses">
                                            {{ getStartDate() }}
                                        </p>
                                    </div>
                                    <span class="font-bold">
                                        -
                                    </span>
                                    <div>
                                        <p
                                            class="font-bold time-end"
                                            :class="stepClasses">
                                            {{ getEndTime() }}
                                        </p>
                                        <p
                                            class="time-end"
                                            :class="stepClasses">
                                            {{ getEndDate() }}
                                        </p>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </template>
                </div>
                <div class="in-line" />
            </div>
            <div
                class="step-item"
                :class="stepClasses">
                <div
                    class="step-circle"
                    @click="clickStep(stage)">
                    <hts-icons
                        v-if="!expanded"
                        svg="plus"
                        color="black"
                        class="w-6 h-6 cursor-pointer" />
                    <hts-icons
                        v-else
                        svg="minus"
                        color="black"
                        class="w-6 h-6 cursor-pointer" />
                </div>
                <div class="step-line step-line-start" />
                <div class="step-line step-line-end" />
                <img
                    v-if="!expanded"
                    class="step-vehicle"
                    :src="vehicleImg" >
            </div>
            <div
                class="right"
                :class="stepClasses">
                <div class="in-step">
                    <div class="flex items-center flex-wrap gap-2 w-full">
                        <div
                            v-if="stageProcess === 'in'"
                            class="w-5 h-5">
                            <img
                                class="w-full"
                                :src="vehicleImg" >
                        </div>
                        <span class="text-gray-600 font-bold max-w-[70%] overflow-hidden text-ellipsis whitespace-nowrap">
                            {{ stage.nodeName }}
                        </span>
                        <div class="relative">
                            <span class="absolute left-0 -translate-y-3 flex gap-1">
                                <hts-icons
                                    v-if="stage.processAlertStatus > 30"
                                    svg="warning-sign"
                                    class="inline-block"
                                    color="red" />
                                <div
                                    v-if="hasWaitingTasks && isValidWaitingTime(stage)"
                                    class="inline-flex items-center">
                                    <hts-icons
                                        svg="hourglass"
                                        fill-color="orange"
                                        class="inline-block" />
                                    <span class="inline-block whitespace-nowrap">
                                        {{ $formatters.duration(stage.waitingTime, 's') }}
                                    </span>
                                </div>
                                <hts-icons
                                    v-if="stage.driverAlert"
                                    svg="driver"
                                    color="purple"
                                    class="inline-block" />
                                <hts-icons
                                    v-if="stage.fileUpdateCount"
                                    svg="document"
                                    class="inline-block cursor-pointer"
                                    @click="handleViewFile" />
                            </span>
                        </div>
                    </div>

                    <p>
                        {{ stage.locationName }}
                    </p>
                    <div class="flex select-none items-center gap-1">
                        <span
                            v-if="workingTimes"
                            class="rounded-full border px-1 cursor-pointer"
                            @click="workingTimeVisible = true">
                            <hts-tooltip>
                                <span>
                                    Thời gian làm việc
                                </span>
                                <template #reference>
                                    <hts-icons
                                        size="mini"
                                        icon-name="hi-calendar-days-icon" />
                                </template>
                            </hts-tooltip>
                        </span>
                        <span
                            v-if="workingOvertime"
                            class="rounded-full border px-2 bg-cyan-700 text-white text-xs">
                            Làm việc ngoài giờ
                        </span>
                        <span
                            v-if="holidayWorking"
                            class="rounded-full border px-2 bg-rose-600 text-white text-xs">
                            Làm việc ngày lễ
                        </span>
                        <span
                            v-if="holidayWorkingOvertime"
                            class="rounded-full border px-2 bg-rose-600 text-white text-xs">
                            Làm việc ngoài giờ ngày lễ
                        </span>
                    </div>
                </div>
                <div class="in-line" />
                <div class="right" />
            </div>
        </div>
        <div class="task-group">
            <template
                v-for="(task, taskIndex) in tasks"
                :key="taskIndex">
                <hts-travel-details-task-item
                    v-if="taskVisible(task)"
                    :task="task"
                    :task-index="taskIndex"
                    :next-task="tasks[taskIndex + 1] || null"
                    :is-last-stage="isLastStage"
                    :is-last="taskIndex === tasks.length - 1"
                    :edit-time="editTime"
                    :has-in-error="stageErrorList.includes(taskIndex + '-in')"
                    :has-out-error="stageErrorList.includes(taskIndex + '-out')"
                    :clearable="clearable"
                    :show-vehicle="cShowVehicle && lastTaskIndex === taskIndex"
                    @update-time="handleChangeTaskTime($event, taskIndex)" />
            </template>
        </div>

        <hts-modal v-model="workingTimeVisible">
            <working-time
                v-if="workingTimes"
                :working-times="workingTimes" />
        </hts-modal>

        <hts-modal
            v-model="viewFile"
            size="large"
            title="Xem tài liệu">
            <div v-if="fileViews.length">
                <!-- Header Desktop -->
                <div class="hidden md:grid grid-cols-6 gap-2 p-2 font-medium border-b bg-gray-100">
                    <div>
                        Tên file
                    </div>
                    <div>
                        Ngày tạo
                    </div>
                    <div>
                        Tác vụ
                    </div>
                    <div>
                        Tạo bởi
                    </div>
                    <div>
                        Xem file
                    </div>
                    <div>
                        Hành động
                    </div>
                </div>

                <!-- Content -->
                <template
                    v-for="(file, index) in fileViews"
                    :key="index">
                    <!-- Desktop Row -->
                    <div class="hidden md:grid grid-cols-6 gap-2 p-2 border-b items-center">
                        <div class="truncate">
                            <hts-tooltip reference-class="truncate">
                                <span>
                                    {{ file.originalName || file.fileOriginName || file.fileName }}
                                </span>
                                <template #reference>
                                    <span class="truncate">
                                        {{ file.originalName || file.fileOriginName || file.fileName }}
                                    </span>
                                </template>
                            </hts-tooltip>
                        </div>
                        <div>
                            {{ $formatters.datetime(file.createdAt) }}
                        </div>
                        <div>
                            {{ file.taskName || 'Khác' }}
                        </div>
                        <div>
                            <hts-user
                                :id="file.createdBy"
                                inline
                                size="mini" />
                        </div>
                        <div>
                            <a
                                :href="$formatters.cdnUrl(file.path)"
                                target="_blank">
                                <img
                                    v-if="file.mimeType && file.mimeType.includes('image')"
                                    :src="$formatters.cdnUrl(file.path)"
                                    class="max-h-20 max-w-full shadow border rounded" >
                                <span
                                    v-else
                                    class="underline text-blue-500">
                                    Mở file
                                </span>
                            </a>
                        </div>
                        <div class="flex gap-2">
                            <button
                                class="text-blue-500 hover:text-blue-800"
                                @click="downloadFile(file)">
                                <hi-arrow-down-tray-icon class="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    <!-- Mobile Card -->
                    <div class="block md:hidden p-3 border rounded shadow mt-2 space-y-2">
                        <div>
                            <span class="font-semibold">
                                Tên file:
                            </span>
                            <span>
                                {{ file.originalName || file.fileOriginName || file.fileName }}
                            </span>
                        </div>
                        <div>
                            <span class="font-semibold">
                                Ngày tạo:
                            </span>
                            {{ $formatters.datetime(file.createdAt) }}
                        </div>
                        <div>
                            <span class="font-semibold">
                                Tác vụ:
                            </span>
                            {{ file.taskName || 'Khác' }}
                        </div>
                        <div>
                            <span class="font-semibold">
                                Tạo bởi:
                            </span>
                            <hts-user
                                :id="file.createdBy"
                                inline
                                size="mini" />
                        </div>
                        <div>
                            <a
                                :href="file.url"
                                target="_blank">
                                <img
                                    v-if="file.mimeType && file.mimeType.includes('image')"
                                    :src="file.url"
                                    class="max-h-20 max-w-full shadow border rounded" >
                                <span
                                    v-else
                                    class="underline text-blue-500">
                                    Mở file
                                </span>
                            </a>
                        </div>
                        <div class="flex gap-4 pt-2 border-t mt-2">
                            <button
                                class="text-blue-500 hover:text-blue-800 flex items-center gap-1"
                                @click="downloadFile(file)">
                                <hi-arrow-down-tray-icon class="h-5 w-5" /><span>
                                    Tải về
                                </span>
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </hts-modal>
    </div>
</template>

<script setup lang="ts">
import { isValidWaitingTime } from './-utils/timing'
import WorkingTime from './-utils/working-time.vue'

const vehicleImg = ref('/assets/vehicle.png')

const { $dayjs, $formatters } = useNuxtApp()
const store = $store()

const props = defineProps({
  journeyId: String,
  travelNodeDataArr: {
    type: Array as PropType<any[]>,
    default: () => []
  },
  stage: {
    type: Object,
    required: true
  },
  stageIndex: Number,
  nextStage: Object,
  stopAtActual: [String, Number] as PropType<string | number | null>,
  travelDetails: {
    type: Array,
    requried: true
  },
  isLastStage: Boolean,
  expanded: Boolean,
  start: Number,
  end: Number,
  editTime: Boolean,
  showTrip: Boolean,
  dateFormat: String,
  clearable: Boolean,
  errorList: {
    type: Array,
    default: () => []
  },
  itemIndex: {
    type: Number,
    default: 0
  },
  files: { type: Array<any> }
})

const emits = defineEmits(['clickStep', 'update-time'])

const {
  travelNodeDataArr,
  stage,
  nextStage,
  stopAtActual,
  travelDetails,
  editTime,
  expanded,
  start,
  end,
  showTrip,
  dateFormat,
  errorList,
  itemIndex,
  journeyId
} = toRefs(props)

const fileViews = ref<{ [key: string]: any }>([])
const viewFile = ref<boolean>(false)

const hasInError = computed(
  () =>
    errorList.value.includes(itemIndex.value + '-in') ||
    errorList.value.includes(itemIndex.value + '-in-invalid') ||
    errorList.value.includes(itemIndex.value + '-in-gt-start')
)
const hasOutError = computed(
  () =>
    errorList.value.includes(itemIndex.value + '-out') ||
    errorList.value.includes(itemIndex.value + '-out-invalid') ||
    errorList.value.includes(itemIndex.value + '-out-empty') ||
    errorList.value.includes(itemIndex.value + '-out-gt-stop') ||
    errorList.value.includes(itemIndex.value + '-out-task')
)
const nodeData = computed(() => {
  const travelNodeData = travelNodeDataArr.value.find((travel: any) => travel.travelId === stage.value._id)
  if (travelNodeData) {
    return travelNodeData.nodeData
  }
  return null
})
const workingOvertime = computed(() => !!nodeData.value?.workingOverTime)
const holidayWorking = computed(() => !!nodeData.value?.holidayWorking)
const holidayWorkingOvertime = computed(() => !!nodeData.value?.holidayWorkingOverTime)
const workingTimeVisible = ref(false)
const workingTimes = computed(() => nodeData.value?.workingTime)
const isValidTravelRange = computed(() => {
  if (showTrip.value) {
    return true
  }
  const { travelIndex } = stage.value
  return (
    (typeof start?.value !== 'number' || (typeof start?.value === 'number' && travelIndex >= start.value)) &&
    (typeof end?.value !== 'number' || (typeof end?.value === 'number' && travelIndex <= end.value))
  )
})
const tasks = computed<any[]>(() => {
  return (travelDetails?.value || []).filter((item: any, i: number) => {
    if (
      (typeof start?.value !== 'number' || (typeof start?.value === 'number' && item.travelIndex >= start.value)) &&
      (typeof end?.value !== 'number' || (typeof end?.value === 'number' && item.travelIndex <= end.value))
    ) {
      return item.type === 'task' && item.stageIndex === stage?.value?.stageIndex
    }
    return false
  })
})
const lastTaskIndex = computed(() => Array.isArray(tasks.value) && tasks.value.findLastIndex((t: any) => !!t.atd || t.ignoreTime))

const stageErrorList = ref<string[]>([])
const inputValidate = $lodash.debounce(() => {
  stageErrorList.value = []
  tasks.value.forEach((item: any, index: number) => {
    const { ata, atd } = item

    if (atd) {
      if (!ata) {
        stageErrorList.value.push(index + '-out')
      } else if (ata > atd) {
        stageErrorList.value.push(index + '-out')
      }
    }
  })
}, 300)

const taskVisible = (task: any) => {
  if (showTrip.value) {
    return true
  }
  const { travelIndex } = task
  return (
    (typeof start?.value !== 'number' || (typeof start?.value === 'number' && travelIndex >= start.value)) &&
    (typeof end?.value !== 'number' || (typeof end?.value === 'number' && travelIndex <= end.value))
  )
}
const cClass = computed(() => {
  return {
    expanded: expanded.value,
    editing: editTime.value,
    done: ['out', 'done'].includes(stageProcess.value)
  }
})

const stageProcess = computed<any>(() => {
  if (stage.value.atd) {
    if (nextStage?.value?.ata || !nextStage?.value) {
      return 'done'
    }
    if (!nextStage?.value && stopAtActual?.value) {
      return 'done'
    }
    return 'out'
  }
  if (stage.value.ata) {
    return 'in'
  }
  return null
})

const cShowVehicle = computed(() => (nextStage || showTrip) && ['out'].includes(stageProcess.value))
const hasWaitingTasks = computed(() => !!tasks.value.find((t: any) => !t.ata && isValidWaitingTime(t)))
const stepClasses = computed(() => {
  return {
    editing: editTime.value,
    'step-in': stageProcess.value === 'in',
    'step-out': stageProcess.value === 'out',
    'step-done': stageProcess.value === 'done',
    'show-vehicle': cShowVehicle.value && stageProcess.value === 'out'
  }
})

const arrivalTime = computed(() => {
  const { eta, ataEstimate, ata } = stage.value
  return $dayjs(ata || ataEstimate || eta)
})
const departureTime = computed(() => {
  const { etd, atdEstimate, atd } = stage.value
  return $dayjs(atd || atdEstimate || etd)
})
const getStartTime = () => {
  return arrivalTime.value.format('HH:mm')
}
const getEndTime = () => {
  return departureTime.value.format('HH:mm')
}
const getStartDate = () => {
  return $formatters.date(arrivalTime.value, dateFormat?.value)
}
const getEndDate = () => {
  return $formatters.date(departureTime.value, dateFormat?.value)
}
const getTimeRange = () => {
  const start = arrivalTime.value.format('HH:mm')
  const end = departureTime.value.format('HH:mm')
  return start + ' - ' + end
}
const isSameDate = computed(() => {
  return arrivalTime.value.isSame(departureTime.value, 'date')
})
const getDateRange = () => {
  if (arrivalTime.value.isSame(departureTime.value, 'date')) {
    return $formatters.date(arrivalTime.value, dateFormat?.value)
  }
  return $formatters.date(arrivalTime.value, dateFormat?.value) + ' - ' + $formatters.date(departureTime.value, dateFormat?.value)
}
const handleViewFile = () => {
  if (!journeyId?.value) {
    return
  }

  let url = $url.monitoring.journey.file.get

  const auth = store.auth

  const { companyType, workspaceType } = auth
  if (companyType === 'PARTNER' && workspaceType === 'VENDOR') {
    url = $urlPartner.monitoring.journey.file.get
  }

  $api(url, { uriParams: { ID: journeyId.value }, query: { positionId: stage.value._id } }).then(({ data }: any) => {
    if (data) {
      fileViews.value = (data?.value || {}).data || []
      viewFile.value = true
    }
  })
}

const downloadFile = async (file: any) => {
  if (!file || !file.url) {
    return
  }

  const name = file.originalName || file.fileOriginName || file.fileName || 'downloaded_file'

  try {
    const response = await fetch(file.url)
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Download failed:', error)
  }
}

const clickStep = (step: any) => {
  emits('clickStep', step)
}

const handleChangeTime = (val: any, type: string) => {
  emits('update-time', { value: val, type, item: stage.value })
  inputValidate()
}
const handleChangeTaskTime = (val: any, taskIndex: any) => {
  emits('update-time', val)
  inputValidate()
}
</script>
